import {
  createSlice,
  injectSlice,
  PayloadAction,
  createAsyncThunk,
  appLogout,
} from '@bonanza-social/toolkit/state';
import { ApiError } from '@bonanza-social/toolkit/api';
import { PrizeWallCampaignState } from './prizeWallCampaign.types';
import PrizeWallCampaignService from '../../services/PrizeWallCampaignService/PrizeWallCampaignService';

export const fetchPrizeWallCampaign = createAsyncThunk(
  'prizeWallCampaign/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const data = await PrizeWallCampaignService.getCampaign();

      return data;
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        return rejectWithValue(error);
      }

      throw error;
    }
  },
);

const initialState: PrizeWallCampaignState = {
  data: undefined,
  dataState: 'idle',
  campaignEnded: false,
  quickRulesShown: false,
};

const prizeWallCampaign = createSlice({
  name: 'prizeWallCampaign',
  initialState,
  reducers: {
    setQuickRulesShown(state, { payload }: PayloadAction<boolean>) {
      state.quickRulesShown = payload;
    },
    setCampaignEnded(state) {
      state.campaignEnded = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPrizeWallCampaign.pending, (state) => {
      state.dataState = 'pending';
    });

    builder.addCase(fetchPrizeWallCampaign.fulfilled, (state, { payload }) => {
      state.data = payload;
      state.dataState = 'fulfilled';
      state.campaignEnded = false;
    });

    builder.addCase(fetchPrizeWallCampaign.rejected, (state) => {
      state.dataState = 'rejected';
    });

    builder.addCase(appLogout, (state) => {
      Object.assign(state, initialState);
    });
  },
});

export const { setQuickRulesShown, setCampaignEnded } =
  prizeWallCampaign.actions;

export const withPrizeWallCampaign = injectSlice({
  reducerPath: 'prizeWallCampaign',
  reducer: prizeWallCampaign.reducer,
});

export const selectPrizeWallCampaignDataState = withPrizeWallCampaign.selector(
  (state) => state.prizeWallCampaign.dataState,
);

export const selectPrizeWallCampaignQuickRulesShown =
  withPrizeWallCampaign.selector(
    (state) => state.prizeWallCampaign.quickRulesShown,
  );

export const selectPrizeWallCampaignEnded = withPrizeWallCampaign.selector(
  (state) => state.prizeWallCampaign.campaignEnded,
);

export const selectHasPrizeWallCampaign = withPrizeWallCampaign.selector(
  (state) => state.prizeWallCampaign.data !== undefined,
);

export const selectHasActivePrizeWallCampaign = withPrizeWallCampaign.selector(
  (state) =>
    selectHasPrizeWallCampaign(state) && !state.prizeWallCampaign.campaignEnded,
);

export const selectPrizeWallCampaignId = withPrizeWallCampaign.selector(
  (state) => state.prizeWallCampaign.data?.id,
);

export const selectPrizeWallCampaignName = withPrizeWallCampaign.selector(
  (state) => state.prizeWallCampaign.data?.name,
);

export const selectPrizeWallCampaignPromoIcon = withPrizeWallCampaign.selector(
  (state) => state.prizeWallCampaign.data?.images.promotionalIcon,
);

export const selectPrizeWallCampaignPromoImage = withPrizeWallCampaign.selector(
  (state) => state.prizeWallCampaign.data?.images.promotionalImage,
);

export const selectPrizeWallCampaignInfoBannerImage =
  withPrizeWallCampaign.selector(
    (state) => state.prizeWallCampaign.data?.images.infoBannerImage,
  );

export const selectPrizeWallCampaignQuickRulesBackgroundImage =
  withPrizeWallCampaign.selector(
    (state) => state.prizeWallCampaign.data?.images.quickRulesBackgroundImage,
  );

export const selectPrizeWallCampaignInfoBackgroundImage =
  withPrizeWallCampaign.selector(
    (state) => state.prizeWallCampaign.data?.images.infoBackgroundImage,
  );

export const selectPrizeWallCampaignBackgroundImage =
  withPrizeWallCampaign.selector(
    (state) => state.prizeWallCampaign.data?.images.promotionalBackgroundImage,
  );

export const selectPrizeWallCampaignRibbonImage =
  withPrizeWallCampaign.selector(
    (state) => state.prizeWallCampaign.data?.images.ribbonImage,
  );

export const selectPrizeWallCampaignWinnersBackgroundImage =
  withPrizeWallCampaign.selector(
    (state) => state.prizeWallCampaign.data?.images.winnersBackgroundImage,
  );

export const selectPrizeWallCampaignTermsAndConditions =
  withPrizeWallCampaign.selector(
    (state) => state.prizeWallCampaign.data?.termsAndConditions,
  );

declare global {
  interface LazyLoadedSlices {
    prizeWallCampaign: ReturnType<typeof prizeWallCampaign.reducer>;
  }
}
